import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { getStorage, ref, uploadBytes } from 'firebase/storage';
import { firestore } from '@/utils/firebase';
import { downloadBlob } from '@/utils/downloadBlob';
import { userImportStoragePath } from '@/utils/storagePaths';
import { adminApi } from '../../api/adminApi';
import { invalidateCrmUsers } from '../../api/crmCaches';

/** A userImports doc's status (kept the same as the backend's services/database/userImports). */
export const USER_IMPORT_STATUSES = {
	queued: 'queued',
	running: 'running',
	finished: 'finished',
	failed: 'failed',
};

/** Whether a user import has stopped running: finished, or failed. */
export const isUserImportDone = ({ status }) =>
	status === USER_IMPORT_STATUSES.finished || status === USER_IMPORT_STATUSES.failed;

// A userImports doc with its Timestamps as ISO strings, so it can be cached
const toUserImport = (snapshot) => {
	const { createdAt, startedAt, finishedAt, lastUpdated, log = [], ...data } = snapshot.data();
	return {
		id: snapshot.id,
		...data,
		createdAt: createdAt?.toDate().toISOString(),
		startedAt: startedAt?.toDate().toISOString(),
		finishedAt: finishedAt?.toDate().toISOString(),
		lastUpdated: lastUpdated?.toDate().toISOString(),
		log: log.map(({ at, ...entry }) => ({ ...entry, at: at.toDate().toISOString() })),
	};
};

/**
 * Admin user import endpoints: the template, reading a file to review, starting an import,
 * and following one as it runs. On the same admin API as the users endpoints (usersApi.js), so they share its cache
 * and tags.
 */
export const adminUserImportsApi = adminApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * Downloads the user import template (GET /users/batch/template, built by the
		 * backend from its import columns) as Folio_User_Import_Template.xlsx.
		 */
		downloadUserImportTemplate: builder.mutation({
			queryFn: async (arg, api, extraOptions, baseQuery) => {
				const result = await baseQuery({
					url: '/users/batch/template',
					responseHandler: (response) => response.blob(),
				});
				if (result.error) {
					return result;
				}
				downloadBlob(result.data, 'Folio_User_Import_Template.xlsx');
				return { data: null };
			},
		}),
		/**
		 * The first step of a user import: uploads the file to Storage
		 * (userImportStoragePath), then POST /users/batch/read reads its rows. Resolves to {
		 * storagePath, fileName, rows: [{ rowNumber, values }] } for the admin to review; a
		 * file that can't be imported (e.g. a missing required column) is a 400 with { error }.
		 */
		readUserImport: builder.mutation({
			queryFn: async (file, api, extraOptions, baseQuery) => {
				const storagePath = userImportStoragePath(crypto.randomUUID(), file.name);
				try {
					await uploadBytes(ref(getStorage(), storagePath), file);
				} catch (error) {
					console.error('Could not upload the import file', error);
					return { error: { status: 'CUSTOM_ERROR', error: error.message } };
				}
				const result = await baseQuery({
					url: '/users/batch/read',
					method: 'POST',
					body: { storagePath },
				});
				if (result.error) {
					return result;
				}
				return { data: { storagePath, fileName: file.name, rows: result.data } };
			},
		}),
		/**
		 * Starts a user import from the reviewed rows: POST /users/batch/create with {
		 * storagePath, fileName, rows, editedCount, removedCount }, which checks the rows again
		 * and queues the import to run in the background. Responds with the userImports doc ({
		 * id, status: 'queued', … }); rows with errors are a 400 with { error, rows: [{
		 * rowNumber, status, errors, warnings }] }.
		 */
		startUserImport: builder.mutation({
			query: (body) => ({ url: '/users/batch/create', method: 'POST', body }),
			// The import history lists it
			invalidatesTags: [{ type: 'ADMIN_IMPORT', id: 'LIST' }],
		}),
		/**
		 * One user import (the userImports doc, see the backend's users/batchCreate), kept up
		 * to date from Firestore while it's in use, so its status, progress and counts change
		 * as it runs. Timestamps are ISO strings. Once it finishes with users created, the
		 * users list and the CRM's users refresh.
		 */
		getUserImport: builder.query({
			queryFn: async (importId) => {
				try {
					const snapshot = await getDoc(doc(firestore, 'userImports', importId));
					return { data: snapshot.exists() ? toUserImport(snapshot) : null };
				} catch (error) {
					console.warn('Could not read the user import', error);
					return { error: { status: 'CUSTOM_ERROR', error: error.message } };
				}
			},
			async onCacheEntryAdded(
				importId,
				{ dispatch, getCacheEntry, updateCachedData, cacheDataLoaded, cacheEntryRemoved },
			) {
				try {
					await cacheDataLoaded;
				} catch {
					// The query reports its own error
					return;
				}
				const unsubscribe = onSnapshot(
					doc(firestore, 'userImports', importId),
					(snapshot) => {
						const userImport = snapshot.exists() ? toUserImport(snapshot) : null;
						const wasFinished =
							getCacheEntry().data?.status === USER_IMPORT_STATUSES.finished;
						updateCachedData(() => userImport);
						if (
							!wasFinished &&
							userImport?.status === USER_IMPORT_STATUSES.finished &&
							userImport.createdCount
						) {
							dispatch(adminApi.util.invalidateTags([{ type: 'ADMIN_USER', id: 'LIST' }]));
							invalidateCrmUsers(dispatch);
						}
					},
					(error) => console.warn('Stopped following the user import', error),
				);
				await cacheEntryRemoved;
				unsubscribe();
			},
		}),
	}),
});

export const {
	useDownloadUserImportTemplateMutation,
	useReadUserImportMutation,
	useStartUserImportMutation,
	useGetUserImportQuery,
} = adminUserImportsApi;
